import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { DashboardShell } from "@/components/DashboardShell";
import { getBillingStatus } from "@/lib/billing.functions";
import { submitEnterpriseInquiry } from "@/lib/contact.functions";
import { createPortalSession, changeSubscriptionPlan } from "@/utils/payments.functions";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useSubscription } from "@/hooks/useSubscription";
import { getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useHydratedTranslation } from "@/components/marketing/Layout";

import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/_authenticated/billing")({
  head: () =>
    buildSeo({
      path: "/billing",
      title: "Billing — Reality Twin",
      description:
        "View your Reality Twin subscription, upgrade or downgrade plans, manage payment methods and download invoices from your billing hub.",
      noindex: true,
    }),
  component: Page,
});

type BillingCycle = "monthly" | "yearly";
type Currency = "usd" | "chf" | "eur";
const CURRENCY_SYMBOL: Record<Currency, string> = { usd: "$", chf: "CHF ", eur: "€" };

const PLANS: Array<{
  id: string;
  name: string;
  amount: { monthly: number; yearly: number } | null;
  limit: string;
  features: string[];
  lookup?: Record<Currency, { monthly: string; yearly: string }>;
}> = [
  {
    id: "free",
    name: "Free",
    amount: null,
    limit: "50 conversations / mo",
    features: ["1 twin", "Shared conversation pool", "Community support"],
  },
  {
    id: "starter",
    name: "Starter",
    amount: { monthly: 39, yearly: 390 },
    limit: "1,000 conversations / mo",
    features: [
      "Up to 3 twins",
      "One shared conversation pool",
      "Lead capture & scoring",
      "Buy extra conversations",
      "Email support",
    ],
    lookup: {
      usd: { monthly: "starter_monthly", yearly: "starter_yearly" },
      chf: { monthly: "starter_monthly_chf", yearly: "starter_yearly_chf" },
      eur: { monthly: "starter_monthly_eur", yearly: "starter_yearly_eur" },
    },
  },
  {
    id: "professional",
    name: "Pro",
    amount: { monthly: 149, yearly: 1490 },
    limit: "5,000 conversations / mo",
    features: [
      "Unlimited twins",
      "One shared conversation pool",
      "Twin-to-Twin protocol",
      "Opportunity engine",
      "Priority support",
    ],
    lookup: {
      usd: { monthly: "pro_monthly", yearly: "pro_yearly" },
      chf: { monthly: "pro_monthly_chf", yearly: "pro_yearly_chf" },
      eur: { monthly: "pro_monthly_eur", yearly: "pro_yearly_eur" },
    },
  },
  {
    id: "scale",
    name: "Scale",
    amount: { monthly: 399, yearly: 3990 },
    limit: "20,000 conversations / mo",
    features: [
      "Everything in Pro",
      "Highest conversation volume",
      "Buy extra conversations",
      "Priority support",
    ],
    lookup: {
      usd: { monthly: "scale_monthly", yearly: "scale_yearly" },
      chf: { monthly: "scale_monthly_chf", yearly: "scale_yearly_chf" },
      eur: { monthly: "scale_monthly_eur", yearly: "scale_yearly_eur" },
    },
  },
];

function Page() {
  const t = useHydratedTranslation();
  const fetchBilling = useServerFn(getBillingStatus);
  const contact = useServerFn(submitEnterpriseInquiry);
  const portalFn = useServerFn(createPortalSession);
  const changePlanFn = useServerFn(changeSubscriptionPlan);
  const [state, setState] = useState<any>(null);
  const [userId, setUserId] = useState<string | undefined>();
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [showContact, setShowContact] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [changing, setChanging] = useState(false);
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const [currency, setCurrency] = useState<Currency>("chf");
  const { openCheckout, checkoutElement, closeCheckout, isOpen } = useStripeCheckout();
  const { subscription, isActive, isPastDue, refetch: refetchSub } = useSubscription(userId);

  const refreshBilling = () => fetchBilling().then(setState);
  useEffect(() => {
    refreshBilling();
  }, [fetchBilling]);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id);
      setUserEmail(data.user?.email ?? undefined);
    });
  }, []);

  const pct = state ? Math.min(100, Math.round((state.used / Math.max(1, state.limit)) * 100)) : 0;

  async function sendInquiry(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    try {
      await contact({ data: form });
      toast.success(t("app.pages.billing.inquiryReceived"));
      setShowContact(false);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  }

  async function openPortal() {
    try {
      const res = await portalFn({
        data: { environment: getStripeEnvironment(), returnUrl: window.location.href },
      });
      if ("error" in res) throw new Error(res.error);
      window.open(res.url, "_blank");
    } catch (e: any) {
      toast.error(e.message ?? t("app.pages.billing.couldNotOpenPortal"));
    }
  }

  function upgradeTo(plan: (typeof PLANS)[number]) {
    if (!plan.lookup) return;
    if (!userId) {
      toast.error(t("app.pages.billing.signInFirst"));
      return;
    }
    openCheckout({
      priceId: plan.lookup[currency][cycle],
    });
  }

  async function switchPlan(plan: (typeof PLANS)[number]) {
    if (!plan.lookup) return;
    setChanging(true);
    try {
      const res = await changePlanFn({
        data: { priceId: plan.lookup[currency][cycle], environment: getStripeEnvironment() },
      });
      if ("error" in res) throw new Error(res.error);
      toast.success(t("app.pages.billing.switchedTo", { plan: plan.name }));
      // Give the webhook a beat, then refresh both hooks.
      setTimeout(() => {
        refetchSub();
        refreshBilling();
      }, 1500);
    } catch (e: any) {
      toast.error(e.message ?? t("app.pages.billing.couldNotChangePlan"));
    } finally {
      setChanging(false);
    }
  }

  return (
    <DashboardShell title={t("app.pages.billing.title")} subtitle={t("app.pages.billing.subtitle")}>
      {isPastDue && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium text-amber-100">
              {t("app.pages.billing.pastDueTitle")}
            </div>
            <div className="text-xs text-amber-200/80 mt-1">
              {t("app.pages.billing.pastDueBody")}
            </div>
            <button
              onClick={openPortal}
              className="mt-3 inline-flex items-center rounded-full bg-amber-400 text-black px-4 py-1.5 text-xs font-medium"
            >
              {t("app.pages.billing.updatePayment")}
            </button>
          </div>
        </div>
      )}

      {state && (
        <div className="glass-strong rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                {t("app.pages.billing.currentPlan")}
              </div>
              <div className="font-display text-3xl mt-1 capitalize">{state.plan}</div>
              {subscription?.cancel_at_period_end && subscription.current_period_end && (
                <div className="text-xs text-amber-500 mt-1">
                  {t("app.pages.billing.cancelsOn", {
                    date: new Date(subscription.current_period_end).toLocaleDateString(),
                  })}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">
                {t("app.pages.billing.convThisMonth")}
              </div>
              <div className="font-mono text-lg">
                {state.used.toLocaleString()} / {state.limit.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="mt-4 h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            {typeof state.twinCount === "number" && state.twinCount > 1
              ? t("app.pages.billing.sharedAcrossN", { count: state.twinCount })
              : t("app.pages.billing.sharedAcrossAll")}
            {state.resetsAt
              ? t("app.pages.billing.resetsSuffix", {
                  date: new Date(state.resetsAt).toLocaleDateString(),
                })
              : ""}
          </div>
          {pct >= 80 && (
            <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-xs text-amber-200/90">
              {t("app.pages.billing.poolWarning", { pct })}
            </div>
          )}
          {isActive && (
            <button
              onClick={openPortal}
              className="mt-4 text-xs text-[oklch(0.82_0.18_168)] underline"
            >
              {t("app.pages.billing.manageSubscription")}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-3 mb-6">
        <div className="inline-flex items-center rounded-full glass-strong p-1 text-xs">
          <button
            onClick={() => setCycle("monthly")}
            className={`px-4 py-1.5 rounded-full ${cycle === "monthly" ? "bg-white/10" : "text-muted-foreground"}`}
          >
            {t("app.pages.billing.monthly")}
          </button>
          <button
            onClick={() => setCycle("yearly")}
            className={`px-4 py-1.5 rounded-full ${cycle === "yearly" ? "bg-white/10" : "text-muted-foreground"}`}
          >
            {t("app.pages.billing.yearly")}
          </button>
        </div>
        <div className="inline-flex items-center rounded-full glass-strong p-1 text-xs">
          {(["chf", "eur", "usd"] as const).map((c) => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              className={`px-3 py-1.5 rounded-full uppercase ${currency === c ? "bg-white/10" : "text-muted-foreground"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map((p) => {
          const current = state?.plan === p.id;
          const isEnt = p.id === "enterprise";
          const isFree = p.id === "free";
          const shownPrice = p.amount
            ? `${CURRENCY_SYMBOL[currency]}${cycle === "yearly" ? p.amount.yearly : p.amount.monthly}`
            : isEnt
              ? t("app.pages.billing.contact")
              : `${CURRENCY_SYMBOL[currency]}0`;
          const suffix = p.amount
            ? cycle === "yearly"
              ? t("app.pages.billing.perYr")
              : t("app.pages.billing.perMo")
            : "";
          const features = t(`app.pages.billing.plans.${p.id}.features`, {
            returnObjects: true,
          }) as unknown as string[];
          return (
            <div
              key={p.id}
              className={`glass-strong rounded-2xl p-6 relative ${current ? "ring-1 ring-[oklch(0.82_0.18_168)]" : ""}`}
            >
              {current && (
                <span className="absolute top-3 right-3 text-xs text-[oklch(0.82_0.18_168)]">
                  {t("app.pages.billing.current")}
                </span>
              )}
              <div className="font-display text-xl">{p.name}</div>
              <div className="font-display text-3xl mt-2">
                {shownPrice}
                <span className="text-sm text-muted-foreground font-sans">{suffix}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t(`app.pages.billing.plans.${p.id}.limit`)}
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="h-4 w-4 text-[oklch(0.82_0.18_168)] shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled={current || isFree || changing}
                onClick={() => {
                  if (isEnt) return setShowContact(true);
                  // Active subscriber switching to a different paid plan: prorated in-app switch.
                  if (isActive && !current && p.lookup) return switchPlan(p);
                  upgradeTo(p);
                }}
                className="mt-5 w-full rounded-full brand-gradient text-black py-2 text-sm font-medium disabled:opacity-40"
              >
                {current
                  ? t("app.pages.billing.active")
                  : isEnt
                    ? t("app.pages.billing.contactSales")
                    : isFree
                      ? "—"
                      : isActive
                        ? changing
                          ? t("app.pages.billing.switching")
                          : t("app.pages.billing.switchPlan")
                        : t("app.pages.billing.upgrade")}
              </button>
            </div>
          );
        })}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
          onClick={closeCheckout}
        >
          <div
            className="bg-background rounded-xl w-full max-w-2xl mt-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-5 py-3 border-b border-border">
              <div className="text-sm font-medium">{t("app.pages.billing.checkout")}</div>
              <button
                onClick={closeCheckout}
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                {t("app.pages.billing.close")}
              </button>
            </div>
            <div className="p-2">{checkoutElement}</div>
          </div>
        </div>
      )}

      {showContact && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowContact(false)}
        >
          <form
            onSubmit={sendInquiry}
            onClick={(e) => e.stopPropagation()}
            className="glass-strong rounded-3xl p-8 max-w-lg w-full space-y-4 relative"
          >
            <button
              type="button"
              onClick={() => setShowContact(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
            <div>
              <div className="font-display text-2xl">{t("app.pages.billing.entTitle")}</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("app.pages.billing.entSub")}
              </div>
            </div>
            <input
              required
              placeholder={t("app.pages.billing.namePh")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inp}
            />
            <input
              required
              type="email"
              placeholder={t("app.pages.billing.emailPh")}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inp}
            />
            <input
              required
              placeholder={t("app.pages.billing.companyPh")}
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className={inp}
            />
            <textarea
              required
              rows={4}
              placeholder={t("app.pages.billing.messagePh")}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={inp}
            />
            <button
              disabled={sending}
              type="submit"
              className="w-full rounded-full brand-gradient text-black py-2.5 text-sm font-medium disabled:opacity-40"
            >
              {sending ? t("app.pages.billing.sending") : t("app.pages.billing.requestCall")}
            </button>
          </form>
        </div>
      )}
    </DashboardShell>
  );
}

const inp =
  "w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]";
